import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, Outlet } from 'react-router-dom';
import {
  LazySuspense,
  ReportGeneratorLazy,
  InvoicesByYearReportLazy,
  InvoicesPaymentAnalysisReportLazy,
  ExpensesByYearReportLazy,
  UnpaidClientsReportLazy,
  AppointmentsReportLazy,
  CallsReportLazy,
  MissedCallsReportLazy,
  MonthlyServicesReportLazy,
  ClientTasksMonthlyReportLazy,
  TodosMonthlyReportPageLazy,
  FilesStoragePageLazy,
  SystemEmailTemplatesPageLazy,
  EmailNotificationSettingsPageLazy,
  DatabaseBrowserPageLazy,
  GemiPreviewPageLazy,
  Directory11888PageLazy,
  PresetsManagementListLazy,
  InvoiceXlsxLazy,
  KnowledgeBaseWikiLayoutLazy,
  KnowledgeBaseWikiHomeLazy,
  KnowledgeBaseArticleViewLazy,
  PortalDashboardLazy,
  PortalInvoicesListLazy,
  PortalInvoiceDetailLazy,
  PortalSupportTicketsListLazy,
  PortalSupportTicketNewLazy,
  PortalSupportTicketDetailLazy,
  PortalServicesListLazy,
  PortalTasksListLazy,
  PortalPackagesListLazy,
  PortalProfileLazy,
} from './lazyRoutes';
import { Form as KnowledgeBaseCategoriesForm, Detail as KnowledgeBaseCategoriesDetail } from './modules/knowledge-base/categories';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { SWRConfigProvider } from './contexts/SWRConfig';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { TopbarProvider } from './contexts/TopbarContext';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalLoading from './components/GlobalLoading';
import Login from './components/Login';
import RegisterFromInvitation from './components/RegisterFromInvitation';
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { List as DashboardList } from './modules/dashboard';
import { List, Form, Detail, ClientSummaryPage } from './modules/clients';
import { List as ClientContactsList, Form as ClientContactsForm, Detail as ClientContactsDetail } from './modules/client-contacts';
import { List as TodosList, Form as TodosForm, Detail as TodosDetail, TodosCalendarView } from './modules/crm-todos';
import { List as AppointmentsList, Form as AppointmentsForm, Detail as AppointmentsDetail, CalendarView as AppointmentsCalendarView } from './modules/crm-appointments';
import { List as CallsList, Form as CallsForm, Detail as CallsDetail } from './modules/crm-calls';
import { List as LeadsList, Form as LeadsForm, Detail as LeadsDetail } from './modules/crm-leads';
import { List as MarketingList, Form as MarketingForm, Detail as MarketingDetail } from './modules/marketing';
import { List as RemindersList, Form as RemindersForm, Detail as RemindersDetail } from './modules/reminders';
import { List as CrmNotesList, Form as CrmNotesForm, Detail as CrmNotesDetail } from './modules/crm-notes';
import { List as ClientServicesList, Form as ClientServicesForm, Detail as ClientServicesDetail, ClientServicesYearlyView } from './modules/client-services';
import { List as ServiceRenewalRemindersList } from './modules/service-renewal-reminders';
import { List as InvoicePaymentsList, Form as InvoicePaymentsForm, Detail as InvoicePaymentsDetail } from './modules/client-invoice-payments';
import { List as ClientInvoicesList, Form as ClientInvoicesForm, Detail as ClientInvoicesDetail } from './modules/client-invoices';
import { List as SuppliersList, Form as SuppliersForm, Detail as SuppliersDetail } from './modules/suppliers';
import { List as SupplierInvoicesList, Form as SupplierInvoicesForm, Detail as SupplierInvoicesDetail } from './modules/supplier-invoices';
import { List as SupplierInvoicePaymentsList, Form as SupplierInvoicePaymentsForm, Detail as SupplierInvoicePaymentsDetail } from './modules/supplier-invoice-payments';
import { List as SupportTicketsList, Form as SupportTicketsForm, Detail as SupportTicketsDetail } from './modules/crm-support-ticket';
import { List as CrmTasksList, Form as CrmTasksForm, Detail as CrmTasksDetail, TasksMonthlyView } from './modules/crm-tasks';
import { List as ConversationsList, Form as ConversationsForm, Detail as ConversationsDetail } from './modules/conversations';
import { List as ActivityLogsList, Detail as ActivityLogsDetail } from './modules/activity-logs';
import { List as NotificationsList, Form as NotificationsForm } from './modules/notifications-management';
import { List as WorkflowsList, Form as WorkflowsForm, Detail as WorkflowsDetail } from './modules/client-workflows';
import WorkflowBuilder from './modules/client-workflows/WorkflowBuilder';
import { Form as KnowledgeBaseForm } from './modules/knowledge-base';
import { List as CustomFieldsList, Form as CustomFieldsForm, Detail as CustomFieldsDetail, FieldConfigurator } from './modules/custom-fields';
import { CardboardCrmNotes } from './modules/cardboard-crm-notes';
import { List as PayablesList, Form as PayablesForm, Detail as PayablesDetail } from './modules/payables';
import { List as OffersList, Form as OffersForm, Detail as OffersDetail } from './modules/offers';
import { List as OfferTemplatesList, Form as OfferTemplatesForm, Detail as OfferTemplatesDetail } from './modules/offer-templates';
import { List as OfferPdfLayoutsList, Form as OfferPdfLayoutsForm } from './modules/offer-pdf-layouts';
import { List as CatalogItemsList, Form as CatalogItemsForm, Detail as CatalogItemsDetail } from './modules/catalog-items';
import { List as RecurringPayablesList, Form as RecurringPayablesForm, Detail as RecurringPayablesDetail } from './modules/recurring-payables';
import { List as PayeesProjectionList } from './modules/payees-projection';
import { ConfigurationSettingsPage } from './modules/configuration-settings';
import { List as SupportAgreementsList, Form as SupportAgreementsForm, Detail as SupportAgreementsDetail } from './modules/crm-support-agreements';
import { List as AllRemindersList } from './modules/all-reminders';
import { List as ProjectsList, Form as ProjectsForm, Detail as ProjectsDetail } from './modules/crm-projects';
import { List as PackagesList, Form as PackagesForm, Detail as PackagesDetail } from './modules/crm-packages';
// Parameter modules - Support Settings
import { List as TaskTypesList, Form as TaskTypesForm, Detail as TaskTypesDetail } from './modules/parameters/task_type';
import { List as TaskCategoriesList, Form as TaskCategoriesForm, Detail as TaskCategoriesDetail } from './modules/parameters/task_category';
import { List as TaskStatusList, Form as TaskStatusForm, Detail as TaskStatusDetail } from './modules/parameters/task_status';
import { List as PrioritiesList, Form as PrioritiesForm, Detail as PrioritiesDetail } from './modules/parameters/priority';
import { List as TicketStatusList, Form as TicketStatusForm, Detail as TicketStatusDetail } from './modules/parameters/ticket_status';
import { List as ManufacturersList, Form as ManufacturersForm, Detail as ManufacturersDetail } from './modules/parameters/manufacturer';
import { List as CatalogCategoriesList, Form as CatalogCategoriesForm, Detail as CatalogCategoriesDetail } from './modules/parameters/catalog-category';
import { List as CallTypesList, Form as CallTypesForm, Detail as CallTypesDetail } from './modules/parameters/call_type';
// Parameter modules - Service Settings
import { List as ServiceTypesList, Form as ServiceTypesForm, Detail as ServiceTypesDetail } from './modules/parameters/service_type';
import { List as FrequencyTypesList, Form as FrequencyTypesForm, Detail as FrequencyTypesDetail } from './modules/parameters/frequency_type';
import { List as DurationsList, Form as DurationsForm, Detail as DurationsDetail } from './modules/parameters/duration';
import { List as ServiceCategoriesList, Form as ServiceCategoriesForm, Detail as ServiceCategoriesDetail } from './modules/parameters/p_crm_service_category';
// Parameter modules - Reminder Settings
import { List as ReminderTypesList, Form as ReminderTypesForm, Detail as ReminderTypesDetail } from './modules/parameters/reminder_type';
// Parameter modules - Business Settings
import { List as TaxAuthoritiesList, Form as TaxAuthoritiesForm, Detail as TaxAuthoritiesDetail } from './modules/parameters/tax_authority';
import { List as CountriesList, Form as CountriesForm, Detail as CountriesDetail } from './modules/parameters/p_country';
import { List as AppStatusList, Form as AppStatusForm, Detail as AppStatusDetail } from './modules/parameters/app_status';
// Parameter modules - Finance Settings
import { List as InvoiceCategoriesList, Form as InvoiceCategoriesForm, Detail as InvoiceCategoriesDetail } from './modules/parameters/p_crm_invoice_category';
import { List as InvoiceTypesList, Form as InvoiceTypesForm, Detail as InvoiceTypesDetail } from './modules/parameters/p_crm_invoice_type';
// Parameter modules - Other
import { List as TagsList, Form as TagsForm, Detail as TagsDetail } from './modules/parameters/tags';
// Expense parameter modules - expense-categories (crm-tasks style: List / Form / Detail)
import { List as ExpenseCategoriesManagerList, Form as ExpenseCategoriesManagerForm, Detail as ExpenseCategoriesManagerDetail } from './modules/expense-categories-manager';
import { List as PayeesList, Form as PayeesForm, Detail as PayeesDetail } from './modules/payees-manager';
import { List as ExpenseCategoriesList, Form as ExpenseCategoriesForm, Detail as ExpenseCategoriesDetail } from './modules/parameters/p_crm_expense_category';
import { List as ExpenseSubCategoriesList, Form as ExpenseSubCategoriesForm, Detail as ExpenseSubCategoriesDetail } from './modules/parameters/p_crm_expense_subcategory';
// Report modules (lazy-loaded via lazyRoutes)
import { List as UsersList, Form as UsersForm, Detail as UsersDetail, UserPermissionsPage } from './modules/users';
import ProfileSettings from './components/ProfileSettings';
import { List as ClientVaultList } from './modules/client-vault';
import { List as VaultTemplatesList } from './modules/vault-templates';
import { PortalAuthProvider, usePortalAuth } from './client-portal/context/PortalAuthContext';
import PortalLogin from './client-portal/login/PortalLogin';
import PortalProtectedRoute from './client-portal/PortalProtectedRoute';

/** Renders register page when URL has token; redirects to / only when logged in and no token (so invite links always open the form). */
const RegisterRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const hasToken = searchParams.get('token');
  if (isAuthenticated && !hasToken) return <Navigate to="/" replace />;
  return <RegisterFromInvitation />;
};

/** Portal login: redirect to dashboard if already authenticated. */
const PortalLoginRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = usePortalAuth();
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/portal/dashboard" replace />;
  return <PortalLogin />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <LazySuspense>
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/register"
        element={<RegisterRoute />}
      />
      <Route path="/portal" element={<PortalAuthProvider><Outlet /></PortalAuthProvider>}>
        <Route path="login" element={<PortalLoginRoute />} />
        <Route element={<PortalProtectedRoute />}>
          <Route index element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="dashboard" element={<PortalDashboardLazy />} />
          <Route path="invoices" element={<PortalInvoicesListLazy />} />
          <Route path="invoices/:id" element={<PortalInvoiceDetailLazy />} />
          <Route path="support" element={<PortalSupportTicketsListLazy />} />
          <Route path="support/new" element={<PortalSupportTicketNewLazy />} />
          <Route path="support/:id" element={<PortalSupportTicketDetailLazy />} />
          <Route path="services" element={<PortalServicesListLazy />} />
          <Route path="tasks" element={<PortalTasksListLazy />} />
          <Route path="packages" element={<PortalPackagesListLazy />} />
          <Route path="profile" element={<PortalProfileLazy />} />
        </Route>
      </Route>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/full"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route
        path="/dashboard-2"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <MainLayout>
              <List />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Form />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Detail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id/summary"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientSummaryPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Form />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-contacts"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientContactsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-contacts/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientContactsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-contacts/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientContactsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-contacts/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientContactsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
            <Route
        path="/todos"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TodosList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/todos/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TodosForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/todos/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TodosDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/todos/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TodosForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppointmentsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppointmentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppointmentsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppointmentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calls"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CallsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calls/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CallsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calls/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CallsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calls/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CallsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LeadsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LeadsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LeadsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LeadsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-vault"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientVaultList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
        <Route
          path="/vault-templates"
          element={
            <ProtectedRoute>
              <MainLayout>
                <VaultTemplatesList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/presets-management"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PresetsManagementListLazy />
              </MainLayout>
            </ProtectedRoute>
          }
        />
      <Route
        path="/marketing"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MarketingList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MarketingForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MarketingDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <MarketingForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RemindersList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RemindersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RemindersDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reminders/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RemindersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm_notes"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrmNotesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm_notes/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrmNotesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm_notes/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrmNotesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm_notes/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrmNotesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-services"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientServicesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-services/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientServicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-services-yearly"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientServicesYearlyView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-services/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientServicesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-services/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientServicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/service_renewal_reminders"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ServiceRenewalRemindersList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice-payments"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoicePaymentsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice-payments/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoicePaymentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice-payments/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoicePaymentsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoice-payments/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoicePaymentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-invoices"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientInvoicesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-invoices/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientInvoicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-invoices/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientInvoicesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-invoices/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ClientInvoicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SuppliersList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SuppliersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SuppliersDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SuppliersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-invoices"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierInvoicesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-invoices/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierInvoicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-invoices/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierInvoicesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-invoices/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierInvoicesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-invoice-payments"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierInvoicePaymentsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-invoice-payments/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierInvoicePaymentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-invoice-payments/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierInvoicePaymentsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-invoice-payments/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupplierInvoicePaymentsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm_tasks"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrmTasksList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm_tasks-monthly"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TasksMonthlyView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm_tasks/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrmTasksForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm_tasks/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrmTasksDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm_tasks/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CrmTasksForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-ticket"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupportTicketsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-ticket/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupportTicketsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-ticket/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupportTicketsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-ticket/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupportTicketsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConversationsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConversationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConversationsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conversations/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConversationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-logs"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ActivityLogsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotificationsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotificationsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/activity-logs/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ActivityLogsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WorkflowsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WorkflowsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WorkflowsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WorkflowsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/workflows/:id/builder"
        element={
          <ProtectedRoute>
            <MainLayout>
              <WorkflowBuilder />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base"
        element={
          <ProtectedRoute>
            <MainLayout>
              <KnowledgeBaseWikiLayoutLazy />
            </MainLayout>
          </ProtectedRoute>
        }
      >
        <Route index element={<KnowledgeBaseWikiHomeLazy />} />
        <Route path="new" element={<KnowledgeBaseForm />} />
        <Route path=":id" element={<KnowledgeBaseArticleViewLazy />} />
        <Route path=":id/edit" element={<KnowledgeBaseForm />} />
        <Route path="categories" element={<Navigate to="/knowledge-base" replace />} />
        <Route path="categories/new" element={<KnowledgeBaseCategoriesForm />} />
        <Route path="categories/:id" element={<KnowledgeBaseCategoriesDetail />} />
        <Route path="categories/:id/edit" element={<KnowledgeBaseCategoriesForm />} />
      </Route>
      <Route
        path="/custom-fields"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CustomFieldsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-fields/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CustomFieldsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-fields/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CustomFieldsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-fields/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CustomFieldsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-fields/:id/configure"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FieldConfigurator />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/expenses" element={<Navigate to="/payables" replace />} />
      <Route path="/expenses/*" element={<Navigate to="/payables" replace />} />
      <Route path="/expenses-summary" element={<Navigate to="/payables" replace />} />
      <Route path="/expenses-grouped" element={<Navigate to="/payables" replace />} />
      <Route path="/expenses-tree" element={<Navigate to="/payables" replace />} />
      <Route path="/expense-projection" element={<Navigate to="/payees-projection" replace />} />
      <Route
        path="/payables"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PayablesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payables/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PayablesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payables/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PayablesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payables/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PayablesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OffersList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OffersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OffersDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offers/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OffersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offer-templates"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OfferTemplatesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offer-templates/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OfferTemplatesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offer-templates/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OfferTemplatesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/offer-templates/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OfferTemplatesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog-items"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CatalogItemsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog-items/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CatalogItemsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog-items/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CatalogItemsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog-items/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CatalogItemsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring-payables"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RecurringPayablesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring-payables/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RecurringPayablesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring-payables/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RecurringPayablesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring-payables/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RecurringPayablesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payees-projection"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PayeesProjectionList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/all-reminders"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AllRemindersList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-agreements"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupportAgreementsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-agreements/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupportAgreementsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-agreements/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupportAgreementsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support-agreements/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SupportAgreementsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectsDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/packages"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PackagesList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/todos_monthly_view"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TodosCalendarView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/todos-monthly" element={<Navigate to="/reports/TodosMonthlyReport" replace />} />
      <Route
        path="/cardboard-crm-notes"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CardboardCrmNotes />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/packages/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PackagesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/packages/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PackagesDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/packages/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <PackagesForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuration-settings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConfigurationSettingsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Parameter modules - Support Settings */}
      <Route path="/task_type" element={<ProtectedRoute><MainLayout><TaskTypesList /></MainLayout></ProtectedRoute>} />
      <Route path="/task_type/new" element={<ProtectedRoute><MainLayout><TaskTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/task_type/:id" element={<ProtectedRoute><MainLayout><TaskTypesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/task_type/:id/edit" element={<ProtectedRoute><MainLayout><TaskTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/task_category" element={<ProtectedRoute><MainLayout><TaskCategoriesList /></MainLayout></ProtectedRoute>} />
      <Route path="/task_category/new" element={<ProtectedRoute><MainLayout><TaskCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/task_category/:id" element={<ProtectedRoute><MainLayout><TaskCategoriesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/task_category/:id/edit" element={<ProtectedRoute><MainLayout><TaskCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/task_status" element={<ProtectedRoute><MainLayout><TaskStatusList /></MainLayout></ProtectedRoute>} />
      <Route path="/task_status/new" element={<ProtectedRoute><MainLayout><TaskStatusForm /></MainLayout></ProtectedRoute>} />
      <Route path="/task_status/:id" element={<ProtectedRoute><MainLayout><TaskStatusDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/task_status/:id/edit" element={<ProtectedRoute><MainLayout><TaskStatusForm /></MainLayout></ProtectedRoute>} />
      <Route path="/priority" element={<ProtectedRoute><MainLayout><PrioritiesList /></MainLayout></ProtectedRoute>} />
      <Route path="/priority/new" element={<ProtectedRoute><MainLayout><PrioritiesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/priority/:id" element={<ProtectedRoute><MainLayout><PrioritiesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/priority/:id/edit" element={<ProtectedRoute><MainLayout><PrioritiesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/manufacturer" element={<ProtectedRoute><MainLayout><ManufacturersList /></MainLayout></ProtectedRoute>} />
      <Route path="/manufacturer/new" element={<ProtectedRoute><MainLayout><ManufacturersForm /></MainLayout></ProtectedRoute>} />
      <Route path="/manufacturer/:id" element={<ProtectedRoute><MainLayout><ManufacturersDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/manufacturer/:id/edit" element={<ProtectedRoute><MainLayout><ManufacturersForm /></MainLayout></ProtectedRoute>} />
      <Route path="/catalog-category" element={<ProtectedRoute><MainLayout><CatalogCategoriesList /></MainLayout></ProtectedRoute>} />
      <Route path="/catalog-category/new" element={<ProtectedRoute><MainLayout><CatalogCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/catalog-category/:id" element={<ProtectedRoute><MainLayout><CatalogCategoriesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/catalog-category/:id/edit" element={<ProtectedRoute><MainLayout><CatalogCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/ticket_status" element={<ProtectedRoute><MainLayout><TicketStatusList /></MainLayout></ProtectedRoute>} />
      <Route path="/ticket_status/new" element={<ProtectedRoute><MainLayout><TicketStatusForm /></MainLayout></ProtectedRoute>} />
      <Route path="/ticket_status/:id" element={<ProtectedRoute><MainLayout><TicketStatusDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/ticket_status/:id/edit" element={<ProtectedRoute><MainLayout><TicketStatusForm /></MainLayout></ProtectedRoute>} />
      <Route path="/call_type" element={<ProtectedRoute><MainLayout><CallTypesList /></MainLayout></ProtectedRoute>} />
      <Route path="/call_type/new" element={<ProtectedRoute><MainLayout><CallTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/call_type/:id" element={<ProtectedRoute><MainLayout><CallTypesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/call_type/:id/edit" element={<ProtectedRoute><MainLayout><CallTypesForm /></MainLayout></ProtectedRoute>} />
      {/* Parameter modules - Service Settings */}
      <Route path="/service_type" element={<ProtectedRoute><MainLayout><ServiceTypesList /></MainLayout></ProtectedRoute>} />
      <Route path="/service_type/new" element={<ProtectedRoute><MainLayout><ServiceTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/service_type/:id" element={<ProtectedRoute><MainLayout><ServiceTypesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/service_type/:id/edit" element={<ProtectedRoute><MainLayout><ServiceTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/frequency_type" element={<ProtectedRoute><MainLayout><FrequencyTypesList /></MainLayout></ProtectedRoute>} />
      <Route path="/frequency_type/new" element={<ProtectedRoute><MainLayout><FrequencyTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/frequency_type/:id" element={<ProtectedRoute><MainLayout><FrequencyTypesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/frequency_type/:id/edit" element={<ProtectedRoute><MainLayout><FrequencyTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/duration" element={<ProtectedRoute><MainLayout><DurationsList /></MainLayout></ProtectedRoute>} />
      <Route path="/duration/new" element={<ProtectedRoute><MainLayout><DurationsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/duration/:id" element={<ProtectedRoute><MainLayout><DurationsDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/duration/:id/edit" element={<ProtectedRoute><MainLayout><DurationsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_service_category" element={<ProtectedRoute><MainLayout><ServiceCategoriesList /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_service_category/new" element={<ProtectedRoute><MainLayout><ServiceCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_service_category/:id" element={<ProtectedRoute><MainLayout><ServiceCategoriesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_service_category/:id/edit" element={<ProtectedRoute><MainLayout><ServiceCategoriesForm /></MainLayout></ProtectedRoute>} />
      {/* Parameter modules - Reminder Settings */}
      <Route path="/reminder_type" element={<ProtectedRoute><MainLayout><ReminderTypesList /></MainLayout></ProtectedRoute>} />
      <Route path="/reminder_type/new" element={<ProtectedRoute><MainLayout><ReminderTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/reminder_type/:id" element={<ProtectedRoute><MainLayout><ReminderTypesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/reminder_type/:id/edit" element={<ProtectedRoute><MainLayout><ReminderTypesForm /></MainLayout></ProtectedRoute>} />
      {/* Parameter modules - Business Settings */}
      <Route path="/tax_authority" element={<ProtectedRoute><MainLayout><TaxAuthoritiesList /></MainLayout></ProtectedRoute>} />
      <Route path="/tax_authority/new" element={<ProtectedRoute><MainLayout><TaxAuthoritiesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/tax_authority/:id" element={<ProtectedRoute><MainLayout><TaxAuthoritiesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/tax_authority/:id/edit" element={<ProtectedRoute><MainLayout><TaxAuthoritiesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_country" element={<ProtectedRoute><MainLayout><CountriesList /></MainLayout></ProtectedRoute>} />
      <Route path="/p_country/new" element={<ProtectedRoute><MainLayout><CountriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_country/:id" element={<ProtectedRoute><MainLayout><CountriesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/p_country/:id/edit" element={<ProtectedRoute><MainLayout><CountriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/app_status" element={<ProtectedRoute><MainLayout><AppStatusList /></MainLayout></ProtectedRoute>} />
      <Route path="/app_status/new" element={<ProtectedRoute><MainLayout><AppStatusForm /></MainLayout></ProtectedRoute>} />
      <Route path="/app_status/:id" element={<ProtectedRoute><MainLayout><AppStatusDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/app_status/:id/edit" element={<ProtectedRoute><MainLayout><AppStatusForm /></MainLayout></ProtectedRoute>} />
      {/* Parameter modules - Finance Settings */}
      <Route path="/p_crm_invoice_category" element={<ProtectedRoute><MainLayout><InvoiceCategoriesList /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_invoice_category/new" element={<ProtectedRoute><MainLayout><InvoiceCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_invoice_category/:id" element={<ProtectedRoute><MainLayout><InvoiceCategoriesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_invoice_category/:id/edit" element={<ProtectedRoute><MainLayout><InvoiceCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_invoice_type" element={<ProtectedRoute><MainLayout><InvoiceTypesList /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_invoice_type/new" element={<ProtectedRoute><MainLayout><InvoiceTypesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_invoice_type/:id" element={<ProtectedRoute><MainLayout><InvoiceTypesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_invoice_type/:id/edit" element={<ProtectedRoute><MainLayout><InvoiceTypesForm /></MainLayout></ProtectedRoute>} />
      {/* Parameter modules - Other */}
      <Route path="/tags" element={<ProtectedRoute><MainLayout><TagsList /></MainLayout></ProtectedRoute>} />
      <Route path="/tags/new" element={<ProtectedRoute><MainLayout><TagsForm /></MainLayout></ProtectedRoute>} />
      <Route path="/tags/:id" element={<ProtectedRoute><MainLayout><TagsDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/tags/:id/edit" element={<ProtectedRoute><MainLayout><TagsForm /></MainLayout></ProtectedRoute>} />
      {/* Expense parameter modules */}
      <Route path="/expense-categories" element={<ProtectedRoute><MainLayout><ExpenseCategoriesManagerList /></MainLayout></ProtectedRoute>} />
      <Route path="/expense-categories/new" element={<ProtectedRoute><MainLayout><ExpenseCategoriesManagerForm /></MainLayout></ProtectedRoute>} />
      <Route path="/expense-categories/:id" element={<ProtectedRoute><MainLayout><ExpenseCategoriesManagerDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/expense-categories/:id/edit" element={<ProtectedRoute><MainLayout><ExpenseCategoriesManagerForm /></MainLayout></ProtectedRoute>} />
      <Route path="/payees" element={<ProtectedRoute><MainLayout><PayeesList /></MainLayout></ProtectedRoute>} />
      <Route path="/payees/new" element={<ProtectedRoute><MainLayout><PayeesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/payees/:id" element={<ProtectedRoute><MainLayout><PayeesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/payees/:id/edit" element={<ProtectedRoute><MainLayout><PayeesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_expense_category" element={<ProtectedRoute><MainLayout><ExpenseCategoriesList /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_expense_category/new" element={<ProtectedRoute><MainLayout><ExpenseCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_expense_category/:id" element={<ProtectedRoute><MainLayout><ExpenseCategoriesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_expense_category/:id/edit" element={<ProtectedRoute><MainLayout><ExpenseCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_expense_subcategory" element={<ProtectedRoute><MainLayout><ExpenseSubCategoriesList /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_expense_subcategory/new" element={<ProtectedRoute><MainLayout><ExpenseSubCategoriesForm /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_expense_subcategory/:id" element={<ProtectedRoute><MainLayout><ExpenseSubCategoriesDetail /></MainLayout></ProtectedRoute>} />
      <Route path="/p_crm_expense_subcategory/:id/edit" element={<ProtectedRoute><MainLayout><ExpenseSubCategoriesForm /></MainLayout></ProtectedRoute>} />
      {/* Report modules */}
      <Route path="/reports/InvoicesByYear" element={<ProtectedRoute><MainLayout><InvoicesByYearReportLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/InvoicesPaymentAnalysis" element={<ProtectedRoute><MainLayout><InvoicesPaymentAnalysisReportLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/ExpensesByYear" element={<ProtectedRoute><MainLayout><ExpensesByYearReportLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/Unpaid" element={<ProtectedRoute><MainLayout><UnpaidClientsReportLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/AppointmentsReport" element={<ProtectedRoute><MainLayout><AppointmentsReportLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/CallsReport" element={<ProtectedRoute><MainLayout><CallsReportLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/MissedCalls" element={<ProtectedRoute><MainLayout><MissedCallsReportLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/MonthlyServices" element={<ProtectedRoute><MainLayout><MonthlyServicesReportLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/ClientTasksMonthly" element={<ProtectedRoute><MainLayout><ClientTasksMonthlyReportLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/TodosMonthlyReport" element={<ProtectedRoute><MainLayout><TodosMonthlyReportPageLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/reports/ReportGenerator" element={<ProtectedRoute><MainLayout><ReportGeneratorLazy /></MainLayout></ProtectedRoute>} />
      <Route path="/report-generator" element={<Navigate to="/reports/ReportGenerator" replace />} />
      <Route
        path="/import/invoice"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoiceXlsxLazy />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/appointments_calendar_view"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppointmentsCalendarView />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Users module routes */}
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsersList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsersDetail />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UsersForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:id/permissions"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserPermissionsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* Profile Settings */}
      <Route
        path="/profile-settings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProfileSettings />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/preferences"
        element={<Navigate to="/profile-settings?tab=preferences" replace />}
      />
      <Route
        path="/settings"
        element={<Navigate to="/profile-settings?tab=application" replace />}
      />
      <Route
        path="/settings/offer-pdf-layouts"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OfferPdfLayoutsList />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/offer-pdf-layouts/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OfferPdfLayoutsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/offer-pdf-layouts/:id/edit"
        element={
          <ProtectedRoute>
            <MainLayout>
              <OfferPdfLayoutsForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin/settings/deleted-files" element={<Navigate to="/admin/settings/files-storage" replace />} />
      <Route
        path="/admin/settings/files-storage"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FilesStoragePageLazy />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/system-email-templates"
        element={
          <ProtectedRoute>
            <MainLayout>
              <SystemEmailTemplatesPageLazy />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/email-notifications"
        element={
          <ProtectedRoute>
            <MainLayout>
              <EmailNotificationSettingsPageLazy />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin/module-management" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/tools/database-browser"
        element={
          <ProtectedRoute>
            <MainLayout>
              <DatabaseBrowserPageLazy />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/gemi-preview"
        element={
          <ProtectedRoute>
            <MainLayout>
              <GemiPreviewPageLazy />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tools/11888-preview"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Directory11888PageLazy />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </LazySuspense>
  );
};

function hardReload() {
  const url = window.location.origin + window.location.pathname + window.location.search;
  window.location.replace(url);
}

const AppErrorFallback = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'system-ui', background: '#f9fafb', color: '#111' }}>
    <div style={{ textAlign: 'center', maxWidth: 400 }}>
      <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Something went wrong</p>
      <p style={{ marginBottom: 16, color: '#666' }}>Reload did not recover. Try &quot;Reload page&quot; below, or close this tab and open the app again.</p>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={hardReload} style={{ padding: '8px 16px', cursor: 'pointer', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8 }}>Reload page</button>
        <button
          type="button"
          onClick={() => window.open(window.location.href, '_blank', 'noopener')}
          style={{ padding: '8px 16px', cursor: 'pointer', background: '#e5e7eb', color: '#111', border: '1px solid #d1d5db', borderRadius: 8 }}
        >
          Open in new tab
        </button>
      </div>
    </div>
  </div>
);

const App: React.FC = () => {
  return (
    <ErrorBoundary fallback={<AppErrorFallback />}>
      <ThemeProvider>
        <NotificationProvider>
          <LoadingProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <SWRConfigProvider>
                <AuthProvider>
                    <SettingsProvider>
                      <TopbarProvider>
                        <GlobalLoading />
                        <AppRoutes />
                      </TopbarProvider>
                    </SettingsProvider>
                </AuthProvider>
              </SWRConfigProvider>
            </Router>
          </LoadingProvider>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;

